import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AssetSelectionComponent } from '@features/shopping/asset/selection/asset-selection.component';
import { ConfirmLineTerminationComponent } from '@features/shopping/confirm-line-termination/confirm-line-termination.component';
import { DeviceAsset, LineAsset, UserAsset } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  constructAdditionalAssetTitle,
  constructAssetTitle,
  getCarrier
} from '@models/functions.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { ClientSettings } from '@models/shopping.model';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ts-activity-4-line-selection',
  templateUrl: './line-selection.component.html',
  imports: [
    CommonModule,
    NgbModalModule,
    AssetSelectionComponent,
    ConfirmLineTerminationComponent,
    TranslationPipe
  ]
})
export class LineSelectionComponent implements OnInit, OnDestroy {
  @ViewChild('confirmLineTerminationModal') confirm_line_termination_template: TemplateRef<any>;
  @ViewChild('multiSimConfirmation') multi_sim_confirmation_template: TemplateRef<any>;
  asset: UserAsset;
  selected_line: LineAsset;
  shopping_cart: ShoppingCartData;
  multi_sim_types: string[] = MULTI_SIM_TYPES;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart = res;
        },
        error: (err: HttpErrorResponse) => console.error(err)
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectAsset(
    asset: UserAsset | LineAsset,
    from_modal: boolean = false,
    is_additional_asset: boolean = false
  ): void {
    if (!is_additional_asset) {
      this.asset = asset as UserAsset;
      this.selected_line = (asset as UserAsset).line;
    } else this.selected_line = asset as LineAsset;

    this.modal_svc.dismissAll();

    if (
      [this.multi_sim_types[0], this.multi_sim_types[1], this.multi_sim_types[6]].includes(
        (asset as UserAsset).multi_sim_type
      ) &&
      !from_modal
    )
      this.modal_svc.open(this.multi_sim_confirmation_template, {
        backdrop: 'static',
        centered: true
      });
    else
      this.retrieveClientSettings(
        is_additional_asset ? this.selected_line : this.asset,
        is_additional_asset
      );
  }

  skipConfirmation(confirm: boolean): void {
    if (confirm) {
      this.modal_svc.dismissAll();

      this.subscriptions.add(
        this.shopping_svc.sendShoppingItems(this.shoppingCartParam(), true).subscribe({
          next: (res: ShoppingCartData) => {
            this.activity_manager_svc.navigateToNextStep();
          },
          error: (err: HttpErrorResponse) => console.error(err)
        })
      );
    }
  }

  constructAssetTitle(include_details: boolean = false): string {
    return constructAssetTitle(this.asset, include_details);
  }

  constructAdditionalAssetTitle(asset: DeviceAsset | LineAsset, type: number): string {
    return constructAdditionalAssetTitle(asset, type);
  }

  calculateModalAdditionalLines(): LineAsset[] {
    return this.asset.additional_lines?.filter(
      (line: LineAsset) => !['Inactive'].includes(line.status)
    )!;
  }

  multisimModalBody(asset: UserAsset): string {
    return asset.multi_sim_type === this.multi_sim_types[0]
      ? this.translation_svc.translate('multisim_device_is', this.constructAssetTitle(true))
      : this.translation_svc.translate('multisim_line_number_is', asset.line.international_number);
  }

  multisimModalPrompt(asset: UserAsset): string {
    return asset.multi_sim_type === this.multi_sim_types[0]
      ? this.translation_svc.translate('multisim_select_eligible_line')
      : this.translation_svc.translate('multisim_devices_affected');
  }

  lineInCart(line: LineAsset): boolean {
    return line.id == this.shopping_cart.existing_asset?.line?.id;
  }

  private retrieveClientSettings(
    asset: UserAsset | LineAsset,
    is_additional_asset: boolean = false
  ): void {
    this.subscriptions.add(
      this.shopping_svc.clientSettings().subscribe({
        next: (res: ClientSettings) => {
          if (is_additional_asset && !(asset as LineAsset)?.contract_end_date) {
            this.skipConfirmation(true);
            return;
          }

          const contract_end_date: Date = is_additional_asset
            ? new Date((asset as LineAsset).contract_end_date!)
            : new Date((asset as UserAsset).line.contract_end_date!);
          const current_date: Date = new Date();

          if (res.notify_user_of_etf && current_date.getTime() <= contract_end_date.getTime())
            this.openConfirmationModal();
          else this.skipConfirmation(true);
        },
        error: (err: HttpErrorResponse) => console.error(err)
      })
    );
  }

  private openConfirmationModal(): void {
    this.modal_svc.open(this.confirm_line_termination_template, {
      backdrop: 'static',
      centered: true,
      size: 'md'
    });
  }

  private shoppingCartParam(): ShoppingCartData {
    const { additional_devices, additional_lines, ...asset_without_additionals }: UserAsset =
      this.asset;
    return {
      ...this.shopping_cart,
      country_id: this.selected_line?.country_id,
      carrier: getCarrier(this.selected_line),
      orderTypeId: this.activity_manager_svc.currentOrderTypeId(),
      lineId: this.selected_line.id,
      existing_asset: {
        ...asset_without_additionals,
        line: this.selected_line,
        asset_from_inventory: true,
        asset_selection_case: 1
      },
      progress_stepper: this.activity_manager_svc
        .state()
        .currentStep(this.activity_manager_svc.state().activeStepIndex())
    };
  }
}
